import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  Container,
  Paper,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useRegisterMutation } from '../store/api/authApi';

const registerSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  username: yup
    .string()
    .min(3, 'Username must be at least 3 characters')
    .required('Username is required')
    .matches(/^[a-z0-9]+$/, 'Username must contain only lowercase letters and numbers, no spaces or special characters'),
  password: yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
  full_name: yup.string().required('Full name is required'),
  umbrella_user_id: yup.string()
    .matches(/^\d{5}$/, 'Umbrella User ID must be exactly 5 digits')
    .required('Umbrella User ID is required'),
});

interface RegisterFormData {
  email: string;
  username: string;
  password: string;
  full_name: string;
  umbrella_user_id: string;
  timezone?: string;
  email_notifications?: boolean;
}

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [register, { isLoading, error, isSuccess }] = useRegisterMutation();
  const [countdown, setCountdown] = useState(5);

  // Countdown timer effect
  useEffect(() => {
    if (isSuccess && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isSuccess && countdown === 0) {
      navigate('/login', { 
        state: { message: 'Registration successful! Please log in.' }
      });
    }
  }, [isSuccess, countdown, navigate]);

  // Function to parse and format error messages
  const getErrorMessage = (error: any): string => {
    try {
      // Handle network errors
      if (error?.status === 'FETCH_ERROR' || error?.status === 'PARSING_ERROR') {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      }

      // Handle HTTP status codes
      if (error?.status) {
        switch (error.status) {
          case 400:
            if (error?.data?.detail) {
              const detail = error.data.detail.toLowerCase();
              if (detail.includes('email already registered')) {
                return 'This email address is already registered. Please use a different email or try logging in.';
              }
              if (detail.includes('username already taken')) {
                return 'This username is already taken. Please choose a different username.';
              }
              if (detail.includes('password must be at least 8 characters')) {
                return 'Password must be at least 8 characters long. Please choose a stronger password.';
              }
              if (detail.includes('username must be at least 3 characters')) {
                return 'Username must be at least 3 characters long. Please choose a longer username.';
              }
              if (detail.includes('invalid email')) {
                return 'Please enter a valid email address.';
              }
              return error.data.detail;
            }
            return 'Invalid registration data. Please check your information and try again.';
          
          case 422:
            return 'Please fill in all required fields correctly. Check that your email is valid and password meets requirements.';
          
          case 500:
            if (error?.data?.detail) {
              return `Server error: ${error.data.detail}. Please try again in a few moments.`;
            }
            return 'Server error occurred. Please try again in a few moments.';
          
          case 503:
            return 'Service temporarily unavailable. Please try again in a few minutes.';
          
          default:
            return `Registration failed (Error ${error.status}). Please try again.`;
        }
      }

      // Handle generic errors
      if (error?.data?.detail) {
        return error.data.detail;
      }

      // Fallback for unknown errors
      return 'Registration failed. Please check your information and try again.';
    } catch {
      return 'An unexpected error occurred. Please try again.';
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: yupResolver(registerSchema),
    defaultValues: {
      email: '',
      username: '',
      password: '',
      full_name: '',
      umbrella_user_id: '',
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      // Ensure all required fields are included
      const registrationData = {
        ...data,
        timezone: data.timezone || "UTC",
        email_notifications: data.email_notifications !== undefined ? data.email_notifications : true
      };
      
      console.log('Registration data being sent:', registrationData);
      console.log('API URL:', process.env.REACT_APP_API_URL);
      
      await register(registrationData).unwrap();
      // Registration successful - the success state will handle the redirect with countdown
    } catch (err) {
      console.error('Registration error:', err);
      // Error is handled by the mutation
    }
  };

  if (isSuccess) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Box textAlign="center">
              <Typography component="h1" variant="h4" gutterBottom color="success.main">
                🎉 Registration Successful!
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                Your account has been created successfully. You can now log in to access your dashboard.
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Redirecting to login page in {countdown} second{countdown !== 1 ? 's' : ''}...
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="contained"
                  color="primary"
                >
                  Go to Login Now
                </Button>
                <Button
                  component={RouterLink}
                  to="/"
                  variant="outlined"
                  color="primary"
                >
                  Go to Home
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography component="h1" variant="h4" gutterBottom>
            HubOne Tools
            </Typography>
            <Typography component="h2" variant="h5" color="text.secondary" gutterBottom>
              Create Account
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {getErrorMessage(error)}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
              <Controller
                name="full_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    label="Full Name"
                    autoComplete="name"
                    autoFocus
                    error={!!errors.full_name}
                    helperText={errors.full_name?.message}
                  />
                )}
              />

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    label="Email Address"
                    type="email"
                    autoComplete="email"
                    placeholder="Enter your email address"
                    error={!!errors.email}
                    helperText={errors.email?.message || "We'll use this to send you important updates"}
                  />
                )}
              />
              
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    label="Username"
                    autoComplete="username"
                    placeholder="Choose a unique username (3+ characters, lowercase, no spaces or special characters)"
                    error={!!errors.username}
                    helperText={errors.username?.message || "Must be at least 3 characters long, only lowercase letters and numbers allowed"}
                    onChange={e => {
                      // Remove spaces and special characters, convert to lowercase
                      const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
                      field.onChange(value);
                    }}
                    value={field.value}
                  />
                )}
              />
              
              <Controller
                name="umbrella_user_id"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    label="Umbrella User ID"
                    placeholder="Enter your 5-digit Umbrella User ID"
                    error={!!errors.umbrella_user_id}
                    helperText={errors.umbrella_user_id?.message || "Must be exactly 5 digits"}
                    inputProps={{ maxLength: 5 }}
                  />
                )}
              />
              
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    label="Password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Create a strong password (8+ characters)"
                    error={!!errors.password}
                    helperText={errors.password?.message || "Must be at least 8 characters long"}
                  />
                )}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Create Account'}
              </Button>
              
              <Box textAlign="center">
                <Link component={RouterLink} to="/login" variant="body2">
                  Already have an account? Sign In
                </Link>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
